import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';
import moment from 'moment-timezone';

import { CurrentUserSelectors, GuestSelectors, UserSelectors } from '@core/account-data';
import { CandidateSelectors } from '@core/candidate-data';
import type {
  InterviewResult,
  InterviewResultMeta,
  InterviewResultResponseSingle,
  Params,
} from '@core/interview-results-types';
import type { CorrelationId, EntityState } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  DEFAULT_SCOPE_NAME,
  getEntityCollectionActions,
  objectToScope,
  pipeState,
} from '@core/ngrx-legacy';
import { JobsWrapperDataService, StageSelectors } from '@core/offers-data';
import { TalentPoolsBriefSelectors } from '@core/talent-pools-data';
import { groupByKey } from '@core/utils-legacy';

import type { InterviewResultsReferences } from '../types';

type State = EntityState<InterviewResult, { scopeMeta: InterviewResultMeta }>;

const idSelector = ({ id }: { id: number | string }) => id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<
  InterviewResult,
  { scopeMeta: InterviewResultMeta }
>()('InterviewResults', {
  idSelector,
  references: makeRef => [
    makeRef('guestId', 'guest', GuestSelectors.byId),
    makeRef('adminId', 'admin', UserSelectors.byId),
    makeRef('candidateId', 'candidate', CandidateSelectors.byId),
    makeRef('offerId', 'offer', offerId => JobsWrapperDataService.jobItemByIdSelector(offerId!)!),
    makeRef('talentPoolId', 'talentPool', talentPoolId =>
      TalentPoolsBriefSelectors.byId(talentPoolId),
    ),
    makeRef('stageId', 'stage', StageSelectors.byId),
  ],

  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => {
    const defaultActions = getEntityCollectionActions(adapter, idSelector);

    const fetchCollection = (
      state: State,
      payload: { params: { scope: string } & Params; replace?: boolean },
    ) =>
      pipeState(
        state,
        next => (payload.replace ? adapter.resetAll(next) : next),
        next =>
          adapter.markAsPending(next, {
            scope: objectToScope(payload.params),
            entities: [],
          }),
      );
    const fetchCollectionSuccess = (
      state: State,
      payload: {
        data: InterviewResult[];
        meta: InterviewResultMeta;
        references: InterviewResultsReferences[];
        scope: string;
        replace?: boolean;
      },
    ) =>
      pipeState(
        state,
        next => adapter.setScopeMetaCustom(next, payload.meta, { scope: payload.scope }),
        next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
        next => adapter.setMany(next, payload.data, { scope: payload.scope }),
        next =>
          adapter.markAsLoaded(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: payload.data.map(e => e.id),
          }),
      );

    return {
      fetchCollection,
      fetchCollectionSuccess: fetchCollectionSuccess,

      fetchCollectionBuffered: fetchCollection,
      fetchCollectionBufferedSuccess: fetchCollectionSuccess,
      fetchCollectionBufferedFailed: defaultActions.fetchCollectionFailed,

      fetchItem: (state, payload: { id: number; scope?: string }) => adapter.markAsPending(state),
      fetchItemSuccess: (state, payload: { data: InterviewResultResponseSingle; scope?: string }) =>
        pipeState(
          state,
          next =>
            adapter.addOne(next, payload.data.interviewResult, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
            }),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [payload.data.interviewResult.id],
            }),
        ),
      fetchItemFailed: (state, payload) => defaultActions.fetchOneFailed(state, payload),
      createOnePessimistic: (
        state,
        payload: {
          data: { value: Partial<InterviewResult>; candidateId: number };
          scope?: string;
        } & Required<CorrelationId>,
      ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),
      createOnePessimisticSuccess: (
        state,
        payload: { data: InterviewResult; scope?: string } & Required<CorrelationId>,
      ) =>
        pipeState(
          state,
          next => adapter.addOne(next, payload.data),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      createOnePessimisticFailed: (state, payload: HttpErrorResponse) =>
        adapter.setError(state, payload),

      update: (state, payload: { data: { id: number; value: any }; scope?: string }) =>
        adapter.markAsPending(state),
      updateSuccess: (state, payload: { data: InterviewResult; scope?: string }) =>
        pipeState(
          state,
          next => adapter.updateOne(next, payload.data),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      updateFailed: (state, payload: HttpErrorResponse) => adapter.setError(state, payload),

      deleteOnePessimistic: (
        state,
        payload: {
          data: { id: number };
          scope?: string;
        } & Required<CorrelationId>,
      ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),
      deleteOnePessimisticSuccess: (
        state,
        payload: { data: InterviewResult; scope?: string } & Required<CorrelationId>,
      ) =>
        pipeState(
          state,
          next => adapter.removeOne(next, payload.data.id),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      deleteOnePessimisticFailed: (state, payload: HttpErrorResponse) =>
        adapter.setError(state, payload),
    };
  },
});

export const interviewResultsCandidateScope = (candidateId: number) => `candidate_${candidateId}`;

export const currentUserLastCandidateRating = (candidateId: number) =>
  createSelector(CurrentUserSelectors.data, selectors.allEntities, (user, entities) => {
    if (!user || !entities?.length) {
      return null;
    }

    return (
      entities
        .filter(entity => entity.candidateId === candidateId && user.id === entity.adminId)
        .sort((a, b) => (moment(a.updatedAt).isAfter(b.updatedAt) ? -1 : 1))[0] ?? null
    );
  });

export const interviewResultsGroupedByInterviewers = (candidateId: number) =>
  createSelector(selectors.allEntitiesWithReferences, (entities: InterviewResult[]) => {
    if (!entities?.length) {
      return <Record<number, InterviewResult[]>>{};
    }

    const currentCandidateResults = entities.filter(
      (entity: InterviewResult) =>
        entity.candidateId === candidateId && ['rating', 'template_form'].includes(entity.kind),
    );

    const userResults = currentCandidateResults.filter(result => result.adminId);
    const guestResults = currentCandidateResults.filter(result => result.guestId);
    const deletedUserResults = currentCandidateResults.filter(
      result => !result.adminId && !result.guestId,
    );
    return {
      ...groupByKey(userResults, 'adminId'),
      ...groupByKey(guestResults, 'guestId'),
      [-1]: deletedUserResults, // treat results from deleted users as from one user (we don't know how to distinguish them)
    };
  });

const InterviewResultsSelectors = {
  ...selectors,
  currentUserLastCandidateRating,
  interviewResultsGroupedByInterviewers,
};

export {
  actions as InterviewResultsActions,
  InterviewResultsSelectors,
  reducer as InterviewResultsReducer,
  namespace as InterviewResultsNamespace,
};
